import type { ReactElement } from 'react';
import type { OnlineUserDto } from '@gr/core/platform/admin/dto';
import { useT } from '../../../../app/di';
import { formatDateTime, formatNumber } from '../../../../shared/format';
import { Badge } from '../../../../ui-kit/Badge/Badge';
import { DataTable } from '../../../../ui-kit/DataTable/DataTable';
import { EmptyState } from '../../../../ui-kit/EmptyState/EmptyState';

export interface PlatformOnlineProps {
  users: readonly OnlineUserDto[];
}

/** ADM-UC-3, ADM-UC-3.CC-1: пользователи с открытым приложением — строка на пользователя, вкладки и устройства в одной строке. */
export function PlatformOnline({ users }: PlatformOnlineProps): ReactElement {
  const { t, locale } = useT();
  return (
    <div className="u-stack">
      <p className="u-muted u-small" aria-live="polite">
        {t('admin.online.count', { n: formatNumber(users.length, locale) })}
      </p>
      <DataTable
        label={t('admin.online.title')}
        rows={users}
        rowKey={(user) => user.userId}
        empty={<EmptyState title={t('admin.online.empty')} />}
        columns={[
          {
            key: 'user',
            header: t('admin.online.user'),
            primary: true,
            cell: (user) => (
              <span className="u-row">
                <span>
                  {user.displayName} <span className="u-muted">· {user.login}</span>
                </span>
                {user.isAdmin ? <Badge tone="accent">{t('admin.online.admin')}</Badge> : null}
              </span>
            ),
          },
          { key: 'connections', header: t('admin.online.connections'), align: 'end', cell: (user) => formatNumber(user.connections, locale) },
          { key: 'blogs', header: t('admin.online.blogs'), cell: (user) => user.blogs.map((blog) => blog.name).join(', ') || '—' },
          { key: 'connected', header: t('admin.online.connectedAt'), cell: (user) => formatDateTime(user.connectedAt, locale) },
          { key: 'seen', header: t('admin.online.lastSeenAt'), cell: (user) => formatDateTime(user.lastSeenAt, locale) },
        ]}
      />
    </div>
  );
}
